import {CollapsibleSidebar} from '@github-ui/layout-primitives/CollapsibleSidebar'
import {FilteredListPageLayout} from '@github-ui/layout-primitives/FilteredListPageLayout'
import {CollapsibleFeedbackLink} from '@github-ui/repos-issues-sidebar-ui/CollapsibleFeedbackLink'
import {SIDEBAR_STORAGE_KEY} from '@github-ui/repos-issues-sidebar-ui/constants'
import {ThreePanesLayout} from '@github-ui/three-panes-layout'
import type {ReactNode} from 'react'

import {useRepoIssuesSidebarPanes} from './use-repo-issues-sidebar-panes'
import {useInitialSidebarExpanded} from './use-initial-sidebar-expanded'
import {useRepoIssuesSidebarLayoutEnabled} from './use-sidebar-layout-enabled'

const SIDEBAR_ARIA_LABEL = 'Issues sidebar navigation'
const DEFAULT_SIDEBAR_DIALOG_TITLE = 'Issues'

export interface RepoIssuesPageLayoutProps {
  children: ReactNode
  /** Page title for the header. Pass `null` (or omit when no actions either) to suppress the header. */
  title?: ReactNode
  /** Trailing actions for the page header (typically a primary CTA). */
  actions?: ReactNode
  /** Content rendered above the title (typically a back-link or breadcrumbs). */
  contextArea?: ReactNode
  /** Title for the narrow-viewport sidebar Dialog. */
  dialogTitle?: string
  /** Render the sidebar content. Receives `true` when the sidebar is collapsible. */
  renderSidebar: (collapsible?: boolean) => ReactNode
  /**
   * Server-read seed for the sidebar's initial expanded state (prevents a flash
   * on hard navigation). Data-router pages pass it from the `issuesLayoutRoute`
   * payload; navigator pages omit it and use the app-payload seed.
   */
  initialExpanded?: boolean
}

/**
 * Layout wrapper for every repo-scoped issues page. When
 * `repo_issues_sidebar_layout` is on, renders the new
 * `<FilteredListPageLayout>` (a `SidebarPageLayout`-composed template that
 * additionally provides `FilterBar` / `Results` / `HeaderActions` slots —
 * page-level slots are adopted by `IssueIndexPage` and the migrated
 * Labels/Milestones/Projects pages). An optional `<PageHeader>` is rendered
 * if a `title` or `actions` prop is passed. When the flag is off, falls
 * back to the legacy `<ThreePanesLayout>` via `useRepoIssuesSidebarPanes()`
 * (which itself respects `issue_pinned_views` for the sidebar).
 */
export function RepoIssuesPageLayout({
  children,
  title,
  actions,
  contextArea,
  dialogTitle = DEFAULT_SIDEBAR_DIALOG_TITLE,
  renderSidebar,
  initialExpanded: initialExpandedProp,
}: RepoIssuesPageLayoutProps) {
  const useSidebarPageLayout = useRepoIssuesSidebarLayoutEnabled()
  const showHeader = useSidebarPageLayout && (title != null || actions != null || contextArea != null)

  const appPayloadInitialExpanded = useInitialSidebarExpanded()
  const initialExpanded = initialExpandedProp ?? appPayloadInitialExpanded
  const panes = useRepoIssuesSidebarPanes(renderSidebar, initialExpanded)

  if (useSidebarPageLayout) {
    return (
      <FilteredListPageLayout storageKey={SIDEBAR_STORAGE_KEY} initialExpanded={initialExpanded}>
        {showHeader ? (
          <FilteredListPageLayout.Header>
            <FilteredListPageLayout.PageHeader>
              {contextArea != null && (
                <FilteredListPageLayout.PageHeader.ContextArea hidden={false}>
                  {contextArea}
                </FilteredListPageLayout.PageHeader.ContextArea>
              )}
              {title != null && (
                <FilteredListPageLayout.PageHeader.TitleArea>
                  <FilteredListPageLayout.PageHeader.Title as="h1">{title}</FilteredListPageLayout.PageHeader.Title>
                </FilteredListPageLayout.PageHeader.TitleArea>
              )}
              {actions != null && (
                <FilteredListPageLayout.PageHeader.Actions>{actions}</FilteredListPageLayout.PageHeader.Actions>
              )}
            </FilteredListPageLayout.PageHeader>
          </FilteredListPageLayout.Header>
        ) : null}
        <FilteredListPageLayout.Sidebar
          aria-label={SIDEBAR_ARIA_LABEL}
          dialogTitle={dialogTitle}
          footer={() => (
            <>
              <CollapsibleFeedbackLink />
              <CollapsibleSidebar.Toggle />
            </>
          )}
        >
          {renderSidebar(true)}
        </FilteredListPageLayout.Sidebar>
        <FilteredListPageLayout.Content as="div">{children}</FilteredListPageLayout.Content>
      </FilteredListPageLayout>
    )
  }

  return (
    <ThreePanesLayout
      contentAs="div"
      resizeable={false}
      leftPaneWidth="medium"
      collapsibleLeftPane={panes.collapsibleLeftPane}
      middlePane={<>{children}</>}
    />
  )
}
